import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/v3/Card';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useGetProviderDirectoryOrganizationsQuery,
  useGetProviderDirectoryServiceQuery,
  useUpdateProviderDirectoryLocationMutation,
} from '@/services/api/providerDirectoryV2Service';
import type { ProviderDirectoryV2AddressSnippet } from '@/services/types/provider-directory-v2-types';

import { CreateOrganizationModal } from './CreateOrganizationModal';

const formatAddressLine = (addr?: ProviderDirectoryV2AddressSnippet | null) => {
  if (!addr) return null;
  const parts = [
    addr.address_line1,
    addr.city,
    addr.state,
    addr.postal_code,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : (addr.formatted_address ?? null);
};

type Slot = 'clinical' | 'billing' | 'records';

export const OrgLinkModal = ({
  open,
  onClose,
  serviceId,
}: {
  open: boolean;
  onClose: () => void;
  serviceId: string;
}) => {
  const { data: service } = useGetProviderDirectoryServiceQuery(
    { serviceId },
    { skip: !open || !serviceId },
  );

  const [orgQ, setOrgQ] = useState('');
  const debouncedOrgQ = useDebouncedValue(orgQ.trim(), 200);
  const orgs = useGetProviderDirectoryOrganizationsQuery(
    { q: debouncedOrgQ || undefined, limit: 50, offset: 0 },
    { skip: !open },
  );

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [linkMode, setLinkMode] = useState<'link' | 'unlink'>('link');
  const [checked, setChecked] = useState<Record<Slot, boolean>>({
    clinical: true,
    billing: true,
    records: true,
  });
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false);

  const [updateLocation, { isLoading: isSaving }] =
    useUpdateProviderDirectoryLocationMutation();

  useEffect(() => {
    if (!open) return;
    setOrgQ('');
    setSelectedOrgId(null);
    setLinkMode('link');
    setChecked({ clinical: true, billing: true, records: true });
  }, [open]);

  const available = useMemo(() => {
    const clinical = service?.clinical_location ?? null;
    const billing = service?.billing_location ?? null;
    const records = service?.records_location ?? null;
    return { clinical, billing, records };
  }, [service]);

  const canSave = useMemo(() => {
    if (!service) return false;
    const anyChecked =
      (checked.clinical && Boolean(available.clinical)) ||
      (checked.billing && Boolean(available.billing)) ||
      (checked.records && Boolean(available.records));
    if (!anyChecked) return false;
    if (linkMode === 'unlink') return true;
    return Boolean(selectedOrgId);
  }, [available, checked, linkMode, selectedOrgId, service]);

  const handleSave = async () => {
    if (!service) return;
    if (!canSave) return;

    const ops: Array<Promise<unknown>> = [];
    const nextOrgId = linkMode === 'unlink' ? null : selectedOrgId;

    const maybePush = (slot: Slot, locationId: string | null) => {
      if (!locationId) return;
      if (!checked[slot]) return;
      ops.push(
        updateLocation({
          locationId,
          patch: { organization_id: nextOrgId },
        }).unwrap(),
      );
    };

    maybePush('clinical', available.clinical?.id ?? null);
    maybePush('billing', available.billing?.id ?? null);
    maybePush('records', available.records?.id ?? null);

    try {
      await Promise.all(ops);
      toast.success(
        linkMode === 'unlink'
          ? 'Unlinked from organization'
          : 'Linked to organization',
      );
      onClose();
    } catch (err) {
      console.error('Failed to link/unlink org', err);
      toast.error('Failed to update organization links');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
        <DialogContent className="w-[calc(100vw-24px)] max-w-3xl max-h-[calc(100vh-24px)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Link to organization</DialogTitle>
            <DialogDescription>
              Link/unlink the routing nodes for this service to an organization.
            </DialogDescription>
          </DialogHeader>

          {!service ? (
            <div className="text-sm text-zinc-600">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
                <div className="text-xs text-zinc-500">Service</div>
                <div className="text-sm font-medium text-zinc-900">
                  {service.name}
                </div>
              </div>

              <Card className="p-4 border border-zinc-200/70">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-zinc-900">
                      Step 1 — Choose an org
                    </div>
                    <div className="text-xs text-zinc-500">
                      Pick an existing org or create a new one.
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsCreateOrgOpen(true)}
                  >
                    Create org
                  </Button>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setLinkMode('link')}
                    className={
                      linkMode === 'link'
                        ? 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-blue-50 border-blue-200 text-blue-700'
                        : 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                    }
                  >
                    Link
                  </button>
                  <button
                    type="button"
                    onClick={() => setLinkMode('unlink')}
                    className={
                      linkMode === 'unlink'
                        ? 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-blue-50 border-blue-200 text-blue-700'
                        : 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                    }
                  >
                    Unlink (set NULL)
                  </button>
                </div>

                {linkMode === 'link' ? (
                  <>
                    <div className="mt-3">
                      <Input
                        value={orgQ}
                        onChange={(e) => setOrgQ(e.target.value)}
                        placeholder="Search organizations…"
                      />
                    </div>

                    <div className="mt-3 max-h-[220px] overflow-auto space-y-2">
                      {(orgs.data?.results ?? []).map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => setSelectedOrgId(o.id)}
                          className={
                            selectedOrgId === o.id
                              ? 'w-full rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-left'
                              : 'w-full rounded-lg border border-zinc-200 hover:bg-zinc-50 px-3 py-2 text-left'
                          }
                        >
                          <div className="text-sm font-medium text-zinc-900 truncate">
                            {o.name}
                          </div>
                          <div className="text-xs text-zinc-500 truncate mt-0.5">
                            {o.organization_type ?? '—'}
                          </div>
                        </button>
                      ))}
                      {!orgs.isFetching &&
                      (orgs.data?.results ?? []).length === 0 ? (
                        <div className="text-sm text-zinc-600 py-3">
                          No organizations found.
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <div className="mt-3 text-sm text-zinc-600">
                    Unlink will set the organization on selected locations to{' '}
                    <span className="font-semibold">NULL</span>.
                  </div>
                )}
              </Card>

              <Card className="p-4 border border-zinc-200/70">
                <div>
                  <div className="text-sm font-semibold text-zinc-900">
                    Step 2 — Choose what to link
                  </div>
                  <div className="text-xs text-zinc-500">
                    This will affect all services using these locations.
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  {(['clinical', 'billing', 'records'] as Slot[]).map(
                    (slot) => {
                      let loc = available.records;
                      let label = 'Records department';
                      if (slot === 'clinical') {
                        loc = available.clinical;
                        label = 'Clinical location';
                      } else if (slot === 'billing') {
                        loc = available.billing;
                        label = 'Billing department';
                      }

                      const checkboxId = `provider-directory-org-link-${slot}`;
                      return (
                        <div
                          key={slot}
                          className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-3"
                        >
                          <input
                            id={checkboxId}
                            type="checkbox"
                            className="mt-1"
                            checked={checked[slot]}
                            disabled={!loc}
                            onChange={(e) =>
                              setChecked((prev) => ({
                                ...prev,
                                [slot]: e.target.checked,
                              }))
                            }
                          />
                          <label
                            htmlFor={checkboxId}
                            className="min-w-0 cursor-pointer"
                          >
                            <div className="text-xs font-semibold text-zinc-600">
                              {label}
                            </div>
                            <div className="text-sm font-medium text-zinc-900 truncate">
                              {loc?.name ?? '—'}
                            </div>
                            <div className="text-xs text-zinc-500 truncate mt-0.5">
                              {formatAddressLine(loc?.address ?? null) ?? '—'}
                            </div>
                          </label>
                        </div>
                      );
                    },
                  )}
                </div>
              </Card>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={!canSave || isSaving}>
                  Confirm
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CreateOrganizationModal
        open={isCreateOrgOpen}
        onClose={() => setIsCreateOrgOpen(false)}
        onCreated={(orgId) => {
          setSelectedOrgId(orgId);
          setLinkMode('link');
        }}
      />
    </>
  );
};
